import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { PanelHead, Badge, Empty, Field, Btn, Advanced } from "./common";
import { ReviewConfirm } from "./ReviewConfirm";
import { AlertTriangle, FileText, Landmark, Truck, Database, BarChart3, Download, Send, RefreshCw } from "lucide-react";

/* Finance — the business flow on top of existing records: quote → explicit send → e-mail approval → completion →
   invoice → read-only bank import → payment matching → reminders → reports/exports, plus supplier deliveries.
   Every outgoing e-mail goes through the same review → single-use token → idempotent send step. No money moves. */

const money = (v, cur = "GBP") => {
  const n = Number(v || 0);
  const sym = { GBP: "£", EUR: "€", USD: "$" }[cur];
  return sym ? `${n < 0 ? "-" : ""}${sym}${Math.abs(n).toFixed(2)}` : `${n.toFixed(2)} ${cur}`;
};
const errMsg = (e, tx, fb) => tx(e?.response?.data?.detail || e?.message || fb);
const idem = (p) => `${p}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  const isX = /\.xlsx?m?$/i.test(file.name);
  r.onload = () => resolve(isX ? { filename: file.name, content_b64: String(r.result).split(",")[1] || "" } : { filename: file.name, content: String(r.result) });
  r.onerror = () => reject(new Error("Dosya okunamadı"));
  if (isX) r.readAsDataURL(file); else r.readAsText(file);
});

/* Shared confirmed-send step (same server pipeline as the E-mails screen). */
const useMailReview = (onDone) => {
  const { tx } = useI18n();
  const [review, setReview] = useState(null);
  const open = async (draftId) => {
    try {
      const r = await api.post(`/gmail/drafts/${draftId}/confirm`);
      setReview({ ...r.data.review, confirm_token: r.data.confirm_token, idem: idem("send") });
    } catch (e) { toast.error(errMsg(e, tx, "Onay hazırlanamadı")); }
  };
  const send = async () => {
    try {
      const r = await api.post(`/gmail/drafts/${review.draft_id}/send`, { confirm_token: review.confirm_token, idempotency_key: review.idem });
      toast.success(r.data?.duplicate_suppressed ? tx("Zaten gönderilmişti") : tx("Gönderildi"));
      onDone?.();
    } catch (e) { toast.error(errMsg(e, tx, "Gönderilemedi")); }
    setReview(null);
  };
  const modal = review && (
    <ReviewConfirm testid="finance-mail-review" title={tx("Gönderim onayı")}
      rows={[[tx("Kimden"), review.from], [tx("Kime"), review.to], [tx("Konu"), review.subject], [tx("Ekler"), (review.attachments || []).join(", ")]]}
      body={review.body} warning={tx("Bu e-posta yalnızca bu onayla gönderilir. Bağlı kayıt (teklif/fatura/bakiye) gönderimden hemen önce yeniden kontrol edilir.")}
      confirmLabel={tx("Onayla ve gönder")} onConfirm={send} onCancel={() => setReview(null)} />
  );
  return [modal, open];
};

/* ======================================================================= summary / reports */
const ReportView = () => {
  const { tx } = useI18n();
  const y = new Date().getFullYear();
  const [f, setF] = useState({ date_from: `${y}-01-01`, date_to: new Date().toISOString().slice(0, 10), category: "", vendor: "", project_id: "" });
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try { setErr(null); setRep((await api.get("/biz/reports/summary", { params: f })).data); }
    catch (e) { setErr(errMsg(e, tx, "Rapor alınamadı")); }
  }, [f, tx]);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const exp = (ds, fmt) => window.open(`${API}/biz/export/${ds}?fmt=${fmt}&date_from=${f.date_from}&date_to=${f.date_to}`, "_blank");
  return (
    <div className="space-y-4" data-testid="finance-report">
      <div className="panel p-3 grid sm:grid-cols-5 gap-2 items-end">
        <Field label={tx("Başlangıç")} type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <Field label={tx("Bitiş")} type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        <Field label={tx("Kategori")} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="materials" />
        <Field label={tx("Tedarikçi / firma")} value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} />
        <Btn onClick={load} className="mb-3" data-testid="report-refresh"><RefreshCw size={13} className="inline mr-1" />{tx("Göster")}</Btn>
      </div>
      {err && <div className="text-sm text-red-300">{err}</div>}
      {rep && (
        <>
          {rep.warnings.length > 0 && (
            <div className="panel p-3 border-amber-400/40 bg-amber-500/10" role="alert" data-testid="report-warnings">
              {rep.warnings.map((w) => <div key={w} className="text-sm text-amber-200 flex gap-2"><AlertTriangle size={15} className="shrink-0 mt-0.5" />{tx(w)}</div>)}
            </div>
          )}
          <p className="text-[0.7rem] text-white/45" data-testid="report-disclaimer">{tx(rep.disclaimer.tr)}</p>
          {Object.keys(rep.currencies).length === 0 && <Empty text={tx("Bu aralıkta kayıt yok.")} />}
          {Object.entries(rep.currencies).map(([cur, c]) => (
            <div key={cur} className="panel p-4 space-y-3" data-testid={`report-currency-${cur}`}>
              <div className="flex items-center justify-between"><h3 className="label-eyebrow m-0">{cur}</h3>
                <span className="text-[0.68rem] text-white/40">{tx("Para birimleri çevrilmez")}</span></div>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <div><div className="label-eyebrow">{tx("Kesilen faturalar (tahakkuk)")}</div>
                  <div className="font-mono text-cyan-200 text-lg">{money(c.invoiced.total, cur)}</div>
                  <div className="text-[0.7rem] text-white/50">{c.invoiced.count} {tx("fatura")} · {tx("net")} {money(c.invoiced.net, cur)} · VAT {money(c.invoiced.vat, cur)} · {tx("kredi notu")} {money(c.invoiced.credit_notes, cur)}</div></div>
                <div><div className="label-eyebrow">{tx("Tahsil edilen nakit (banka)")}</div>
                  <div className="font-mono text-emerald-200 text-lg">{money(c.cash_in.total, cur)}</div>
                  <div className="text-[0.7rem] text-white/50">{tx("faturalara eşlenen")} {money(c.cash_in.invoice_receipts, cur)} · {tx("iadeler")} {money(c.cash_in.refunds_received, cur)} · {tx("diğer")} {money(c.cash_in.other, cur)}</div></div>
                <div><div className="label-eyebrow">{tx("Nakit çıkışı / giderler")}</div>
                  <div className="font-mono text-amber-200 text-lg">{money(c.cash_out.total, cur)}</div>
                  <div className="text-[0.7rem] text-white/50">{tx("net nakit akışı")} {money(c.net_cash_flow, cur)} · {tx("transfer hariç")}: {c.transfers_excluded.count} · {tx("bekleyen hariç")}: {c.pending_excluded}</div></div>
              </div>
              <div className="flex flex-wrap gap-1.5 text-[0.7rem]">
                <Badge color="red">{tx("ödenmemiş")} {c.invoice_status.unpaid.count} · {money(c.invoice_status.unpaid.outstanding, cur)}</Badge>
                <Badge color="blue">{tx("kısmi")} {c.invoice_status.partial.count} · {money(c.invoice_status.partial.outstanding, cur)}</Badge>
                <Badge color="green">{tx("ödendi")} {c.invoice_status.paid.count} · {money(c.invoice_status.paid.total, cur)}</Badge>
                {c.invoice_status.cancelled.count > 0 && <Badge color="gray">{tx("iptal")} {c.invoice_status.cancelled.count}</Badge>}
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-xs">
                <div><div className="label-eyebrow mb-1">{tx("Kategoriye göre")}</div>
                  {Object.entries(c.by_category).map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-white/65">{tx(k)}</span><span className="font-mono">{money(v, cur)}</span></div>)}</div>
                <div><div className="label-eyebrow mb-1">{tx("Tedarikçiye göre")}</div>
                  {Object.entries(c.by_vendor).slice(0, 8).map(([k, v]) => <div key={k} className="flex justify-between gap-2"><span className="text-white/65 truncate">{k}</span><span className="font-mono">{money(v, cur)}</span></div>)}</div>
                <div><div className="label-eyebrow mb-1">{tx("Aylık")}</div>
                  {Object.entries(c.by_month).map(([m, v]) => <div key={m} className="flex justify-between gap-2 font-mono"><span className="text-white/55">{m}</span><span className="text-cyan-200">{money(v.invoiced, cur)}</span><span className="text-emerald-200">+{money(v.cash_in, cur)}</span><span className="text-amber-200">-{money(v.cash_out, cur)}</span></div>)}</div>
              </div>
            </div>
          ))}
          {rep.projects.length > 0 && (
            <div className="panel p-4" data-testid="report-projects">
              <div className="label-eyebrow mb-2">{tx("Proje maliyet ve kâr")}</div>
              {rep.projects.map((p) => (
                <div key={`${p.project_id}-${p.currency}`} className="flex flex-wrap items-center justify-between gap-2 text-xs py-1 border-b border-white/5">
                  <span className="text-white/80">{p.name}</span>
                  <span className="font-mono text-white/60">{tx("fatura net")} {money(p.invoiced_net, p.currency)} · {tx("atanan maliyet")} {money(p.attributed_costs, p.currency)}</span>
                  <span className="font-mono">{p.margin === null ? <Badge color="gray">{tx("maliyet atanmadı")}</Badge>
                    : <>{money(p.margin, p.currency)} <Badge color={p.margin_status === "estimated" ? "amber" : "green"}>{tx(p.margin_status === "estimated" ? "tahmini" : "atanmış maliyet")}</Badge></>}</span>
                </div>
              ))}
            </div>
          )}
          {(rep.review.length > 0 || rep.refunds_reversals.length > 0) && (
            <div className="panel p-4 text-xs space-y-1" data-testid="report-review">
              <div className="label-eyebrow mb-1">{tx("İnceleme bekleyenler")}</div>
              {rep.refunds_reversals.map((r) => <div key={r.txn_id} className="text-amber-200">{r.date} · {tx(r.kind)} · {money(r.amount, r.currency)} · {r.description}</div>)}
              {rep.review.slice(0, 30).map((r) => <div key={r.txn_id} className="text-white/60">{r.date} · {tx(r.category)} · {money(r.amount, r.currency)} · {r.description} {r.evidence?.matched ? `(${r.evidence.matched})` : ""}</div>)}
            </div>
          )}
          <div className="panel p-4" data-testid="report-exports">
            <div className="label-eyebrow mb-2">{tx("Dışa aktar (muhasebe için)")}</div>
            {[["report", tx("Rapor")], ["transactions", tx("Banka hareketleri")], ["invoices", tx("Faturalar")], ["payments", tx("Ödemeler")], ["quotes", tx("Teklifler")]].map(([ds, label]) => (
              <div key={ds} className="flex items-center justify-between gap-2 py-1 text-sm">
                <span className="text-white/75">{label}</span>
                <div className="flex gap-1.5">{["csv", "xlsx", "pdf"].map((fmt) => (
                  <button key={fmt} onClick={() => exp(ds, fmt)} data-testid={`export-${ds}-${fmt}`} className="px-2 py-1 rounded border border-cyan-400/25 text-cyan-200 text-xs"><Download size={11} className="inline mr-1" />{fmt.toUpperCase()}</button>))}</div>
              </div>
            ))}
            <p className="text-[0.68rem] text-white/40 mt-2">{tx("Dışa aktarımlar formül enjeksiyonuna karşı güvenlidir; tutarlar 2 ondalıklı sabit sayıdır.")}</p>
          </div>
        </>
      )}
    </div>
  );
};

/* ======================================================================= quote → invoice flow */
const APPROVAL_SOURCES = ["email", "in_person", "phone", "signed_document", "message"];

const FlowView = () => {
  const { tx } = useI18n();
  const [d, setD] = useState(null);
  const [check, setCheck] = useState({});      // proposal_id → {message_id, account_key, result}
  const [appr, setAppr] = useState(null);      // owner approval form
  const [vars, setVars] = useState({});        // quote id → selected variation ids
  const [showPaid, setShowPaid] = useState(false);
  const load = useCallback(async () => { try { setD((await api.get("/biz/flow")).data); } catch (e) { toast.error(errMsg(e, tx, "Yüklenemedi")); } }, [tx]);
  useEffect(() => { load(); }, [load]);
  const [mailModal, openReview] = useMailReview(load);
  const call = async (fn, ok) => { try { const r = await fn(); if (ok) toast.success(tx(ok)); await load(); return r; } catch (e) { toast.error(errMsg(e, tx, "İşlem başarısız")); return null; } };
  if (!d) return <div className="text-sm text-white/40">{tx("Yükleniyor...")}</div>;
  const quotes = d.quotes.filter((q) => !q.parent_quote_id);
  const variations = d.quotes.filter((q) => q.parent_quote_id);
  const unpaid = d.invoices.filter((i) => ["issued"].includes(i.status) && i.invoice_kind !== "credit_note");
  const drafts = d.invoices.filter((i) => i.status === "draft" && i.biz_flow);
  const closed = d.invoices.filter((i) => ["paid", "cancelled"].includes(i.status) || i.invoice_kind === "credit_note");
  const pdf = (i) => window.open(`${API}/proposals/${i.proposal_id}/pdf`, "_blank");

  const prepareQuote = async (q) => {
    const r = await call(() => api.post(`/biz/quotes/${q.proposal_id}/prepare-send`, {}), "Numaralı PDF ve e-posta taslağı hazır");
    if (r?.data?.draft?.draft_id) openReview(r.data.draft.draft_id);
  };
  const runCheck = async (q) => {
    const c = check[q.proposal_id] || {};
    if (!c.message_id) { toast.error(tx("E-posta kimliği girin")); return; }
    const r = await call(() => api.post(`/biz/quotes/${q.proposal_id}/approval-check`, { message_id: c.message_id, account_key: c.account_key || "primary" }));
    if (r) setCheck({ ...check, [q.proposal_id]: { ...c, result: r.data } });
  };
  const saveApproval = async () => {
    await call(() => api.post(`/biz/quotes/${appr.proposal_id}/approval`, { decision: "approve", source_type: appr.source_type, approver: appr.approver, date: appr.date, source_ref: appr.source_ref, check_id: appr.check_id }), "Onay kaydedildi");
    setAppr(null);
  };
  const invoiceFromQuote = (q) => call(() => api.post("/biz/invoices/from-quote", { quote_id: q.proposal_id, variation_ids: vars[q.proposal_id] || [] }), "Fatura taslağı oluşturuldu");
  const issue = (i) => { if (window.confirm(tx("Fatura kesilsin mi? Numara verilir ve PDF kilitlenir; bu numara bir daha kullanılmaz."))) call(() => api.post(`/biz/invoices/${i.proposal_id}/issue`, { confirm: true }), "Fatura kesildi"); };
  const sendInvoice = async (i) => { const r = await call(() => api.post(`/biz/invoices/${i.proposal_id}/prepare-send`, {})); if (r?.data?.draft?.draft_id) openReview(r.data.draft.draft_id); };
  const correct = (i) => {
    const reason = window.prompt(tx("Düzeltme gerekçesi (kredi notu kesilir, yeni taslak açılır):"));
    if (reason && reason.trim().length >= 3) call(() => api.post(`/biz/invoices/${i.proposal_id}/correct`, { reason, confirm: true }), "Kredi notu kesildi; yeni taslak hazır");
  };
  const complete = (p) => { if (window.confirm(tx("Bu projedeki iş tamamlandı olarak işaretlensin mi?"))) call(() => api.post(`/biz/projects/${p.project_id}/complete`, { confirm: true }), "İş tamamlandı"); };

  return (
    <div className="space-y-4" data-testid="finance-flow">
      {mailModal}
      {d.billing_missing.length > 0 && (
        <div className="panel p-3 border-amber-400/40 bg-amber-500/10 text-sm text-amber-200" data-testid="billing-missing">
          <AlertTriangle size={14} className="inline mr-1" />{tx("Fatura kesmek için eksik bilgiler (Ayarlar → Benim işim → Fatura bilgileri):")} {d.billing_missing.map((m) => tx(m)).join(", ")}
        </div>
      )}
      <section className="panel p-4">
        <div className="label-eyebrow mb-2">{tx("Teklifler")}</div>
        {quotes.length === 0 && <Empty text={tx("Teklif yok — Teklifler ekranından oluşturun.")} />}
        {quotes.map((q) => {
          const c = check[q.proposal_id] || {};
          const qVars = variations.filter((v) => v.parent_quote_id === q.proposal_id);
          return (
            <div key={q.proposal_id} className="py-2 border-b border-white/5 text-sm" data-testid="flow-quote">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-white/85">{q.quote_number || q.number || "—"} · {q.client?.name || "—"} · {q.title}</span>
                <span className="flex items-center gap-2"><span className="font-mono text-cyan-200">{money(q.total)}</span>
                  <Badge color={q.status === "accepted" ? "green" : q.status === "sent" ? "amber" : "gray"}>{tx(q.status || "draft")}</Badge>
                  {q.locked && <Badge color="gray">{tx("kilitli")}</Badge>}</span>
              </div>
              {q.approval && <div className="text-[0.7rem] text-emerald-300/80 mt-1">{tx("Onay")}: {tx(q.approval.source_type)} · {q.approval.from || q.approval.approver} · {q.approval.date || q.approval.recorded_at?.slice(0, 10)}{q.approval.excerpt ? ` · “${q.approval.excerpt.slice(0, 90)}”` : ""}</div>}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {!["accepted", "started"].includes(q.status) && <Btn variant="ghost" onClick={() => prepareQuote(q)} data-testid="quote-prepare-send"><Send size={12} className="inline mr-1" />{q.locked ? tx("Yeniden gönder") : tx("PDF + gönder")}</Btn>}
                {q.status === "sent" && <Btn variant="ghost" onClick={() => setAppr({ proposal_id: q.proposal_id, source_type: "in_person", approver: q.client?.name || "", date: new Date().toISOString().slice(0, 10), source_ref: "" })}>{tx("Onayı elle kaydet")}</Btn>}
                {["accepted", "started"].includes(q.status) && (
                  <>
                    {qVars.filter((v) => v.status === "accepted").map((v) => (
                      <label key={v.proposal_id} className="text-[0.7rem] text-white/65 flex items-center gap-1">
                        <input type="checkbox" checked={(vars[q.proposal_id] || []).includes(v.proposal_id)}
                          onChange={(e) => setVars({ ...vars, [q.proposal_id]: e.target.checked ? [...(vars[q.proposal_id] || []), v.proposal_id] : (vars[q.proposal_id] || []).filter((x) => x !== v.proposal_id) })} />
                        {tx("Ek iş")} {v.title} ({money(v.total)})
                      </label>
                    ))}
                    <Btn onClick={() => invoiceFromQuote(q)} data-testid="invoice-from-quote">{tx("Fatura taslağı")}</Btn>
                  </>
                )}
              </div>
              {q.status === "sent" && (
                <div className="flex flex-wrap items-end gap-2 mt-2" data-testid="approval-check">
                  <input value={c.message_id || ""} onChange={(e) => setCheck({ ...check, [q.proposal_id]: { ...c, message_id: e.target.value } })}
                    placeholder={tx("Müşteri yanıtının e-posta kimliği")} aria-label={tx("Müşteri yanıtının e-posta kimliği")}
                    className="bg-black/30 border border-cyan-500/20 rounded-lg px-2 py-1 text-xs text-white flex-1 min-w-[180px]" />
                  <Btn variant="ghost" onClick={() => runCheck(q)}>{tx("Onayı kontrol et")}</Btn>
                </div>
              )}
              {c.result && (
                <div className={`text-[0.7rem] mt-1 ${c.result.decision === "approved" ? "text-emerald-300" : "text-amber-200"}`} data-testid="approval-result">
                  {c.result.decision === "approved" ? tx("Açık onay bulundu — teklif onaylandı.") : tx("İnceleme gerekli — otomatik onaylanmadı.")}
                  {" "}{tx("Sınıf")}: {tx(c.result.classification)} · {(c.result.reasons || []).map((r) => tx(r.split(":")[0])).join(", ")}
                  <div className="text-white/45">“{(c.result.evidence?.excerpt || "").slice(0, 200)}”</div>
                  {c.result.decision !== "approved" && <button className="underline text-cyan-200" onClick={() => setAppr({ proposal_id: q.proposal_id, source_type: "email", approver: c.result.evidence?.from || "", date: new Date().toISOString().slice(0, 10), source_ref: c.result.evidence?.message_id, check_id: c.result.check_id })}>{tx("İnceledim, onay olarak kaydet")}</button>}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {appr && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3" role="dialog" aria-modal="true" data-testid="owner-approval-modal">
          <div className="panel w-full max-w-md p-5 space-y-2">
            <h3 className="label-eyebrow m-0">{tx("Onayı kaydet")}</h3>
            <p className="text-[0.72rem] text-amber-200/90">{tx("Onayı yalnızca siz kaydedebilirsiniz; kaynak, onaylayan kişi ve tarih zorunludur.")}</p>
            <label className="block text-xs text-white/60">{tx("Kaynak")}
              <select value={appr.source_type} onChange={(e) => setAppr({ ...appr, source_type: e.target.value })} className="w-full bg-black/40 border border-cyan-500/20 rounded px-2 py-1.5 text-white">
                {APPROVAL_SOURCES.map((x) => <option key={x} value={x}>{tx(x)}</option>)}</select></label>
            <Field label={tx("Onaylayan")} value={appr.approver} onChange={(e) => setAppr({ ...appr, approver: e.target.value })} />
            <Field label={tx("Tarih")} type="date" value={appr.date} onChange={(e) => setAppr({ ...appr, date: e.target.value })} />
            <Field label={tx("Kaynak referansı")} value={appr.source_ref || ""} onChange={(e) => setAppr({ ...appr, source_ref: e.target.value })} />
            <div className="flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setAppr(null)}>{tx("Vazgeç")}</Btn>
              <Btn onClick={saveApproval} data-testid="owner-approval-save">{tx("Onayı kaydet")}</Btn>
            </div>
          </div>
        </div>
      )}

      <section className="panel p-4">
        <div className="label-eyebrow mb-2">{tx("İş tamamlama")}</div>
        {d.projects.filter((p) => !["completed", "done", "finished"].includes(p.status)).slice(0, 12).map((p) => (
          <div key={p.project_id} className="flex items-center justify-between text-sm py-1"><span className="text-white/75">{p.name}</span>
            <Btn variant="ghost" onClick={() => complete(p)} data-testid="project-complete">{tx("İş tamamlandı")}</Btn></div>
        ))}
      </section>

      <section className="panel p-4" data-testid="flow-invoices">
        <div className="label-eyebrow mb-2">{tx("Faturalar")}</div>
        {drafts.map((i) => (
          <div key={i.proposal_id} className="flex flex-wrap items-center justify-between gap-2 text-sm py-1.5 border-b border-white/5">
            <span className="text-white/80">{tx("Taslak")} · {i.client?.name || "—"} · {i.title}</span>
            <span className="flex items-center gap-2"><span className="font-mono text-cyan-200">{money(i.total, i.currency)}</span>
              <Btn variant="ghost" onClick={() => pdf(i)}>PDF</Btn>
              <Btn onClick={() => issue(i)} disabled={d.billing_missing.length > 0} data-testid="invoice-issue">{tx("Kes (numara ver)")}</Btn></span>
          </div>
        ))}
        {unpaid.length === 0 && drafts.length === 0 && <Empty text={tx("Ödenmemiş fatura yok.")} />}
        {unpaid.map((i) => (
          <div key={i.proposal_id} className="flex flex-wrap items-center justify-between gap-2 text-sm py-1.5 border-b border-white/5" data-testid="flow-unpaid-invoice">
            <span className="text-white/85">{i.number} · {i.client?.name || "—"} · {tx("vade")} {i.due_date}</span>
            <span className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-cyan-200">{money(i.total, i.currency)}</span>
              <Badge color={i.payment_status === "partial" ? "blue" : "red"}>{tx(i.payment_status || "unpaid")} · {tx("kalan")} {money(i.outstanding, i.currency)}</Badge>
              {i.sent_at && <Badge color="gray">{tx("gönderildi")}</Badge>}
              <Btn variant="ghost" onClick={() => pdf(i)}>PDF</Btn>
              <Btn variant="ghost" onClick={() => sendInvoice(i)} data-testid="invoice-send"><Send size={12} className="inline mr-1" />{tx("Gönder")}</Btn>
              <Btn variant="ghost" onClick={() => correct(i)}>{tx("Düzelt")}</Btn>
            </span>
          </div>
        ))}
        <button className="text-[0.7rem] text-white/45 mt-2 underline" onClick={() => setShowPaid(!showPaid)}>{showPaid ? tx("Kapalıları gizle") : tx("Ödenen / iptal / kredi notlarını göster")} ({closed.length})</button>
        {showPaid && closed.map((i) => (
          <div key={i.proposal_id} className="flex items-center justify-between text-xs py-1 text-white/55">
            <span>{i.number} · {i.client?.name || "—"} {i.credits_invoice_number ? `· ${tx("kredi notu")} → ${i.credits_invoice_number}` : ""}</span>
            <span className="flex items-center gap-2"><span className="font-mono">{money(i.total, i.currency)}</span><Badge color={i.status === "paid" ? "green" : "gray"}>{tx(i.status)}</Badge>
              <button onClick={() => pdf(i)} className="underline">PDF</button></span>
          </div>
        ))}
      </section>

      {d.open_reminders.length > 0 && (
        <section className="panel p-4" data-testid="flow-reminders">
          <div className="label-eyebrow mb-2">{tx("Açık hatırlatmalar")}</div>
          {d.open_reminders.map((r) => (
            <div key={r.reminder_id} className="flex items-center justify-between text-sm py-1">
              <span className="text-white/75">{tx(r.kind)} · {r.due_on} · {tx("kalan")} {money(r.remaining)} · <Badge color={r.status === "stale" ? "amber" : "gray"}>{tx(r.status)}</Badge></span>
              {r.draft_id && r.status === "draft_prepared" && <Btn variant="ghost" onClick={() => openReview(r.draft_id)}>{tx("İncele ve gönder")}</Btn>}
            </div>
          ))}
          <p className="text-[0.68rem] text-white/40 mt-1">{tx("Hatırlatmalar otomatik gönderilmez. Gönderimden hemen önce bakiye yeniden okunur; ödenmişse gönderilmez.")}</p>
        </section>
      )}
    </div>
  );
};

/* ======================================================================= bank (read-only) */
const CATS = ["income", "refund", "materials", "subcontractor", "tools", "fuel", "vehicle", "software", "insurance", "telecom",
  "utilities", "rent", "bank_fees", "tax", "food", "office", "card_settlement", "transfer", "drawings", "other", "uncategorized"];

const BankView = () => {
  const { tx } = useI18n();
  const [st, setSt] = useState(null);
  const [accts, setAccts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [txs, setTxs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newAcc, setNewAcc] = useState({ name: "", currency: "GBP", kind: "current" });
  const [imp, setImp] = useState({ account_id: "", file: null, preview: null, mapping: {} });
  const [alloc, setAlloc] = useState({});
  const load = useCallback(async () => {
    try {
      const [s, a, r, t, p] = await Promise.all([api.get("/biz/bank/status"), api.get("/biz/bank/accounts"), api.get("/biz/bank/match/reviews"),
        api.get("/biz/bank/transactions", { params: { needs_review: true } }), api.get("/projects")]);
      setSt(s.data); setAccts(a.data); setReviews(r.data); setTxs(t.data); setProjects(p.data);
    } catch (e) { toast.error(errMsg(e, tx, "Yüklenemedi")); }
  }, [tx]);
  useEffect(() => { load(); }, [load]);
  const addAcc = async () => { try { await api.post("/biz/bank/accounts", newAcc); setNewAcc({ name: "", currency: "GBP", kind: "current" }); load(); } catch (e) { toast.error(errMsg(e, tx, "Kaydedilemedi")); } };
  const preview = async (mapping) => {
    if (!imp.account_id || !imp.file) { toast.error(tx("Hesap ve dosya seçin")); return; }
    try {
      const f = await readFile(imp.file);
      const r = await api.post("/biz/bank/import/preview", { account_id: imp.account_id, ...f, mapping: mapping || imp.mapping });
      setImp({ ...imp, preview: r.data, mapping: r.data.mapping || {} });
    } catch (e) { toast.error(errMsg(e, tx, "Önizleme başarısız")); }
  };
  const confirmImport = async () => {
    try {
      const r = await api.post(`/biz/bank/import/${imp.preview.preview_id}/confirm`, { confirm: true });
      toast.success(`${tx("Eklendi")}: ${r.data.added} · ${tx("yinelenen atlandı")}: ${r.data.duplicates_skipped} · ${tx("otomatik eşlenen")}: ${r.data.auto_allocated.length}`);
      setImp({ account_id: imp.account_id, file: null, preview: null, mapping: {} });
      load();
    } catch (e) { toast.error(errMsg(e, tx, "İçe aktarılamadı")); }
  };
  const doAlloc = async (rv) => {
    const rows = Object.entries(alloc[rv.review_id] || {}).filter(([, v]) => Number(v) > 0).map(([invoice_id, amount]) => ({ invoice_id, amount }));
    if (!rows.length) { toast.error(tx("Tutar girin")); return; }
    try { await api.post("/biz/bank/allocate", { txn_id: rv.txn_id, allocations: rows, idempotency_key: idem(`al_${rv.review_id}`) }); toast.success(tx("Ödeme eşlendi")); load(); }
    catch (e) { toast.error(errMsg(e, tx, "Eşlenemedi")); }
  };
  const override = async (t, patch) => { try { await api.put(`/biz/bank/transactions/${t.txn_id}`, patch); load(); } catch (e) { toast.error(errMsg(e, tx, "Kaydedilemedi")); } };
  const confirmReversal = async (t) => {
    const orig = t.reversal_evidence?.possible_original_txn_id || window.prompt(tx("Geri çevrilen orijinal ödemenin hareket kimliği:"));
    if (!orig) return;
    try { await api.post(`/biz/bank/transactions/${t.txn_id}/reversal`, { reverses_txn_id: orig, confirm: true }); toast.success(tx("Geri çevirme kaydedildi; fatura yeniden açıldı")); load(); }
    catch (e) { toast.error(errMsg(e, tx, "İşlem başarısız")); }
  };
  if (!st) return <div className="text-sm text-white/40">{tx("Yükleniyor...")}</div>;
  const pv = imp.preview;
  return (
    <div className="space-y-4" data-testid="finance-bank">
      <div className="panel p-3 text-sm" data-testid="bank-status">
        <Badge color={st.provider_connected ? "green" : "gray"}>{st.provider_connected ? tx("Sağlayıcı bağlı (salt-okunur)") : tx("Banka sağlayıcısı bağlı değil")}</Badge>
        <Badge color="cyan">{tx("Yalnızca okuma · ödeme/transfer yok")}</Badge>
        <p className="text-[0.72rem] text-white/50 mt-2">{tx(st.note)}</p>
      </div>
      <section className="panel p-4">
        <div className="label-eyebrow mb-2">{tx("Hesaplar")}</div>
        {accts.map((a) => (
          <div key={a.account_id} className="text-sm py-1 flex flex-wrap justify-between gap-2">
            <span className="text-white/80">{a.name} · {a.currency} · {tx(a.kind)}</span>
            <span className="text-[0.7rem] text-white/45">{(a.coverage || []).map((r) => `${r[0]}→${r[1]}`).join(", ") || tx("veri yok")}</span>
          </div>
        ))}
        <div className="grid sm:grid-cols-4 gap-2 items-end mt-2">
          <Field label={tx("Hesap adı")} value={newAcc.name} onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })} />
          <Field label={tx("Para birimi")} value={newAcc.currency} onChange={(e) => setNewAcc({ ...newAcc, currency: e.target.value.toUpperCase() })} />
          <label className="block mb-3 text-xs text-white/60">{tx("Tür")}
            <select value={newAcc.kind} onChange={(e) => setNewAcc({ ...newAcc, kind: e.target.value })} className="w-full bg-black/40 border border-cyan-500/20 rounded px-2 py-2 text-white">
              {["current", "savings", "card", "other"].map((k) => <option key={k} value={k}>{tx(k)}</option>)}</select></label>
          <Btn onClick={addAcc} className="mb-3" data-testid="bank-add-account">{tx("Hesap ekle")}</Btn>
        </div>
      </section>
      <section className="panel p-4" data-testid="bank-import">
        <div className="label-eyebrow mb-2">{tx("Ekstre içe aktar (CSV / Excel)")}</div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={imp.account_id} onChange={(e) => setImp({ ...imp, account_id: e.target.value, preview: null })} aria-label={tx("Hesap")} className="bg-black/40 border border-cyan-500/20 rounded px-2 py-1.5 text-sm text-white">
            <option value="">{tx("Hesap seçin")}</option>{accts.map((a) => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}</select>
          <input type="file" accept=".csv,.xlsx" onChange={(e) => setImp({ ...imp, file: e.target.files?.[0] || null, preview: null })} className="text-xs text-white/70" data-testid="bank-file" />
          <Btn variant="ghost" onClick={() => preview()} data-testid="bank-preview">{tx("Önizle")}</Btn>
        </div>
        {pv?.needs_mapping && (
          <div className="mt-2 grid sm:grid-cols-3 gap-2 text-xs">
            {pv.fields.map((fld) => (
              <label key={fld} className="text-white/60">{fld}
                <select value={imp.mapping[fld] || ""} onChange={(e) => setImp({ ...imp, mapping: { ...imp.mapping, [fld]: e.target.value || null } })} className="w-full bg-black/40 border border-cyan-500/20 rounded px-1 py-1 text-white">
                  <option value="">—</option>{pv.headers.map((h) => <option key={h} value={h}>{h}</option>)}</select></label>
            ))}
            <Btn variant="ghost" onClick={() => preview(imp.mapping)}>{tx("Eşlemeyle önizle")}</Btn>
          </div>
        )}
        {pv?.preview_id && (
          <div className="mt-3 text-xs space-y-2" data-testid="bank-preview-result">
            <div className="flex flex-wrap gap-1.5"><Badge color="green">{tx("yeni")} {pv.counts.new}</Badge><Badge color="gray">{tx("yinelenen")} {pv.counts.duplicate}</Badge>
              <Badge color="red">{tx("hatalı")} {pv.counts.errors}</Badge><Badge color="amber">{tx("bekleyen")} {pv.counts.pending}</Badge>
              <span className="text-white/45">{pv.period.from} → {pv.period.to}</span></div>
            <div className="max-h-56 overflow-y-auto scrollbar-thin">
              {pv.sample.map((r) => <div key={r.row} className={`font-mono ${r.state === "error" ? "text-red-300" : r.state === "duplicate" ? "text-white/35" : "text-white/75"}`}>
                {r.row}. {r.date} {r.amount} {r.currency} · {r.description} {r.status === "pending" ? `(${tx("bekleyen")})` : ""} {r.errors?.length ? `— ${r.errors.join("; ")}` : ""}</div>)}
            </div>
            <Btn onClick={confirmImport} data-testid="bank-confirm-import">{tx("Onayla ve içe aktar")}</Btn>
          </div>
        )}
      </section>
      <section className="panel p-4" data-testid="bank-reviews">
        <div className="label-eyebrow mb-2">{tx("Ödeme eşleme incelemesi")}</div>
        {reviews.length === 0 && <div className="text-xs text-white/45">{tx("İnceleme bekleyen ödeme yok.")}</div>}
        {reviews.map((rv) => (
          <div key={rv.review_id} className="py-2 border-b border-white/5 text-xs space-y-1">
            <div className="text-white/80">{rv.txn?.date} · {money(rv.txn?.amount, rv.currency)} · {rv.txn?.counterparty} · {rv.txn?.reference || rv.txn?.description}</div>
            <div className="flex flex-wrap gap-1">{rv.codes.map((c) => <Badge key={c} color="amber">{tx(c)}</Badge>)}<span className="text-white/45">{tx("eşlenebilir kalan")} {money(rv.remaining, rv.currency)}</span></div>
            {rv.candidates.map((c) => (
              <div key={c.invoice_id} className="flex items-center gap-2">
                <span className="text-white/70 w-40">{c.number} · {tx("kalan")} {money(c.outstanding, c.currency)}</span>
                <span className="text-white/40 flex-1">{c.reasons.map((r) => tx(r)).join(", ")}</span>
                <input type="number" step="0.01" min="0" disabled={!c.same_currency} aria-label={tx("Eşlenecek tutar")}
                  value={(alloc[rv.review_id] || {})[c.invoice_id] || ""} onChange={(e) => setAlloc({ ...alloc, [rv.review_id]: { ...(alloc[rv.review_id] || {}), [c.invoice_id]: e.target.value } })}
                  className="w-24 bg-black/40 border border-cyan-500/20 rounded px-1 py-0.5 text-white" />
              </div>
            ))}
            <div className="flex gap-2"><Btn variant="ghost" onClick={() => doAlloc(rv)} data-testid="bank-allocate">{tx("Eşle")}</Btn>
              <Btn variant="ghost" onClick={() => api.post(`/biz/bank/match/reviews/${rv.review_id}/dismiss`, {}).then(load)}>{tx("Fatura ödemesi değil")}</Btn></div>
          </div>
        ))}
        <p className="text-[0.68rem] text-white/40 mt-1">{tx("Otomatik 'Ödendi' yalnızca tek ve referansı doğrulanmış tam ödemede yapılır; kısmi, fazla, çoklu veya farklı para birimli ödemeler sizin onayınızı bekler.")}</p>
      </section>
      <section className="panel p-4" data-testid="bank-tx-review">
        <div className="label-eyebrow mb-2">{tx("Kategori / transfer / iade incelemesi")}</div>
        {txs.length === 0 && <div className="text-xs text-white/45">{tx("İnceleme bekleyen hareket yok.")}</div>}
        {txs.slice(0, 60).map((t) => (
          <div key={t.txn_id} className="flex flex-wrap items-center gap-2 text-xs py-1 border-b border-white/5">
            <span className="text-white/75 flex-1 min-w-[200px]">{t.date} · {money(t.amount, t.currency)} · {t.description}{t.category_evidence?.matched ? ` (${t.category_evidence.matched})` : ""}</span>
            {t.reversal_review && <Btn variant="ghost" onClick={() => confirmReversal(t)}>{tx("Geri çevirme olarak onayla")}</Btn>}
            <select value={t.category} onChange={(e) => override(t, { category: e.target.value })} aria-label={tx("Kategori")} className="bg-black/40 border border-cyan-500/20 rounded px-1 py-0.5 text-white">
              {CATS.map((c) => <option key={c} value={c}>{tx(c)}</option>)}</select>
            <select value={t.project_id || ""} onChange={(e) => override(t, { project_id: e.target.value || null })} aria-label={tx("Proje")} className="bg-black/40 border border-cyan-500/20 rounded px-1 py-0.5 text-white max-w-[140px]">
              <option value="">{tx("Proje yok")}</option>{projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}</select>
            <label className="text-white/55 flex items-center gap-1"><input type="checkbox" checked={!!t.is_transfer} onChange={(e) => override(t, { is_transfer: e.target.checked })} />{tx("Transfer")}</label>
          </div>
        ))}
        <p className="text-[0.68rem] text-white/40 mt-1">{tx("Her kategori değişikliği önceki değeriyle birlikte denetim kaydına yazılır.")}</p>
      </section>
    </div>
  );
};

/* ======================================================================= suppliers */
const SuppliersView = () => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  const [mid, setMid] = useState("");
  const [man, setMan] = useState({ vendor: "", order_ref: "", promised_date: "", vendor_email: "" });
  const load = useCallback(async () => { try { setS((await api.get("/biz/suppliers/orders")).data); } catch (e) { toast.error(errMsg(e, tx, "Yüklenemedi")); } }, [tx]);
  useEffect(() => { load(); }, [load]);
  const [mailModal, openReview] = useMailReview(load);
  const call = async (fn, ok) => { try { const r = await fn(); if (ok) toast.success(tx(ok)); await load(); return r; } catch (e) { toast.error(errMsg(e, tx, "İşlem başarısız")); return null; } };
  if (!s) return <div className="text-sm text-white/40">{tx("Yükleniyor...")}</div>;
  return (
    <div className="space-y-4" data-testid="finance-suppliers">
      {mailModal}
      <section className="panel p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Btn variant="ghost" onClick={() => call(() => api.post("/biz/suppliers/scan", {}), "Son siparişler tarandı")} data-testid="supplier-scan">{tx("Son e-postaları tara")}</Btn>
          <input value={mid} onChange={(e) => setMid(e.target.value)} placeholder={tx("E-posta kimliği")} aria-label={tx("E-posta kimliği")} className="bg-black/30 border border-cyan-500/20 rounded-lg px-2 py-1 text-xs text-white" />
          <Btn variant="ghost" onClick={() => call(() => api.post("/biz/suppliers/extract", { message_id: mid }), "E-postadan okundu")}>{tx("E-postadan çıkar")}</Btn>
        </div>
        <p className="text-[0.68rem] text-white/40 mt-2">{tx("Sipariş numarası olmayan e-postalardan kayıt oluşturulmaz; belirsiz tarihler işaretlenir, uydurulmaz. Kurye entegrasyonu yoktur.")}</p>
      </section>
      <section className="panel p-4" data-testid="supplier-orders">
        <div className="label-eyebrow mb-2">{tx("Siparişler")}</div>
        {s.orders.length === 0 && <Empty text={tx("Kayıtlı sipariş yok.")} />}
        {s.orders.map((o) => (
          <div key={o.order_id} className="py-2 border-b border-white/5 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-white/85">{o.vendor} · {o.order_ref}</span>
              <span className="flex items-center gap-1.5 flex-wrap">
                <Badge color={o.overdue ? "red" : o.status === "delivered" ? "green" : "gray"}>{tx(o.status)}</Badge>
                {o.overdue && <Badge color="red">{o.days_late} {tx("gün gecikmiş")}</Badge>}
                {!o.date_certain && o.status !== "delivered" && <Badge color="amber">{tx("tarih belirsiz")}</Badge>}
                <span className="text-xs text-white/55">{o.promised_date || tx("tarih yok")}</span>
              </span>
            </div>
            {(o.flags || []).length > 0 && <div className="text-[0.68rem] text-amber-200/70">{o.flags.map((f) => tx(f)).join(", ")}</div>}
            {o.sources?.[0]?.excerpt && <div className="text-[0.68rem] text-white/40">“{o.sources[o.sources.length - 1].excerpt.slice(0, 160)}” · {o.sources[o.sources.length - 1].subject}</div>}
            <div className="flex flex-wrap gap-1.5 mt-1">
              <select value={o.status} onChange={(e) => call(() => api.put(`/biz/suppliers/orders/${o.order_id}`, { status: e.target.value }))} aria-label={tx("Durum")} className="bg-black/40 border border-cyan-500/20 rounded px-1 py-0.5 text-xs text-white">
                {["ordered", "dispatched", "delivered", "delayed", "cancelled"].map((x) => <option key={x} value={x}>{tx(x)}</option>)}</select>
              <input type="date" value={o.promised_date || ""} onChange={(e) => call(() => api.put(`/biz/suppliers/orders/${o.order_id}`, { promised_date: e.target.value || null }))} aria-label={tx("Teslim tarihi")} className="bg-black/40 border border-cyan-500/20 rounded px-1 py-0.5 text-xs text-white" />
              <Btn variant="ghost" onClick={async () => { const r = await call(() => api.post(`/biz/suppliers/orders/${o.order_id}/followup-draft`, {}), "Takip taslağı hazır"); if (r?.data?.draft?.draft_id) openReview(r.data.draft.draft_id); }}>{tx("Takip e-postası")}</Btn>
            </div>
          </div>
        ))}
      </section>
      <section className="panel p-4 grid sm:grid-cols-5 gap-2 items-end">
        <Field label={tx("Tedarikçi")} value={man.vendor} onChange={(e) => setMan({ ...man, vendor: e.target.value })} />
        <Field label={tx("Sipariş no")} value={man.order_ref} onChange={(e) => setMan({ ...man, order_ref: e.target.value })} />
        <Field label={tx("Teslim tarihi")} type="date" value={man.promised_date} onChange={(e) => setMan({ ...man, promised_date: e.target.value })} />
        <Field label={tx("Tedarikçi e-postası")} value={man.vendor_email} onChange={(e) => setMan({ ...man, vendor_email: e.target.value })} />
        <Btn className="mb-3" onClick={() => call(() => api.post("/biz/suppliers/orders", { ...man, promised_date: man.promised_date || null }), "Kaydedildi").then(() => setMan({ vendor: "", order_ref: "", promised_date: "", vendor_email: "" }))}>{tx("Elle ekle")}</Btn>
      </section>
    </div>
  );
};

/* ======================================================================= historical spreadsheet import */
const DataView = () => {
  const { tx } = useI18n();
  const [st, setSt] = useState({ dataset: "invoices", file: null, preview: null, mapping: {}, include: false });
  const preview = async (mapping) => {
    if (!st.file) { toast.error(tx("Dosya seçin")); return; }
    try {
      const f = await readFile(st.file);
      const r = await api.post("/biz/import/preview", { dataset: st.dataset, ...f, mapping: mapping || st.mapping });
      setSt({ ...st, preview: r.data, mapping: r.data.mapping || {} });
    } catch (e) { toast.error(errMsg(e, tx, "Önizleme başarısız")); }
  };
  const confirm = async () => {
    try {
      const r = await api.post(`/biz/import/${st.preview.preview_id}/confirm`, { confirm: true, include_possible_duplicates: st.include });
      toast.success(`${tx("Eklendi")}: ${r.data.added} · ${tx("atlandı")}: ${r.data.skipped}`);
      setSt({ dataset: st.dataset, file: null, preview: null, mapping: {}, include: false });
    } catch (e) { toast.error(errMsg(e, tx, "İçe aktarılamadı")); }
  };
  const pv = st.preview;
  return (
    <div className="space-y-4" data-testid="finance-data">
      <section className="panel p-4">
        <div className="label-eyebrow mb-2">{tx("Geçmiş Excel / CSV içe aktar")}</div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={st.dataset} onChange={(e) => setSt({ ...st, dataset: e.target.value, preview: null })} aria-label={tx("Veri türü")} className="bg-black/40 border border-cyan-500/20 rounded px-2 py-1.5 text-sm text-white">
            <option value="invoices">{tx("Faturalar")}</option><option value="expenses">{tx("Giderler")}</option></select>
          <input type="file" accept=".csv,.xlsx" onChange={(e) => setSt({ ...st, file: e.target.files?.[0] || null, preview: null })} className="text-xs text-white/70" data-testid="sheet-file" />
          <Btn variant="ghost" onClick={() => preview()} data-testid="sheet-preview">{tx("Önizle")}</Btn>
        </div>
        {pv?.needs_mapping && (
          <div className="mt-2 grid sm:grid-cols-3 gap-2 text-xs">
            <div className="sm:col-span-3 text-amber-200">{tx("Zorunlu sütunlar eşlenmedi:")} {pv.missing.join(", ")}</div>
            {pv.fields.map((fld) => (
              <label key={fld} className="text-white/60">{fld}
                <select value={st.mapping[fld] || ""} onChange={(e) => setSt({ ...st, mapping: { ...st.mapping, [fld]: e.target.value || null } })} className="w-full bg-black/40 border border-cyan-500/20 rounded px-1 py-1 text-white">
                  <option value="">—</option>{pv.headers.map((h) => <option key={h} value={h}>{h}</option>)}</select></label>
            ))}
            <Btn variant="ghost" onClick={() => preview(st.mapping)}>{tx("Eşlemeyle önizle")}</Btn>
          </div>
        )}
        {pv?.preview_id && (
          <div className="mt-3 text-xs space-y-2" data-testid="sheet-preview-result">
            <div className="flex flex-wrap gap-1.5">{Object.entries(pv.counts).map(([k, v]) => <Badge key={k} color={k === "new" ? "green" : k === "error" ? "red" : "amber"}>{tx(k)} {v}</Badge>)}</div>
            <div className="max-h-56 overflow-y-auto scrollbar-thin">
              {pv.sample.map((r) => <div key={r.row} className={r.state === "error" ? "text-red-300" : r.state === "new" ? "text-white/75" : "text-white/40"}>
                {r.row}. {r.number || r.vendor || ""} {r.issue_date || r.date} {r.total || r.amount} {r.currency} · {tx(r.state)} {r.errors?.length ? `— ${r.errors.join("; ")}` : ""}</div>)}
            </div>
            {pv.counts.possible_bank_duplicate > 0 && (
              <label className="flex items-center gap-2 text-white/60"><input type="checkbox" checked={st.include} onChange={(e) => setSt({ ...st, include: e.target.checked })} />{tx("Olası banka yinelenenlerini de ekle (önerilmez)")}</label>
            )}
            <Btn onClick={confirm} data-testid="sheet-confirm">{tx("Onayla ve içe aktar")}</Btn>
          </div>
        )}
        <p className="text-[0.68rem] text-white/40 mt-2">{tx("İçe aktarma mevcut kayıtların üzerine yazmaz ve kaynak dosyayı değiştirmez; yinelenenler atlanır.")}</p>
      </section>
    </div>
  );
};

/* ======================================================================= shell */
export const FinancePanel = () => {
  const { tx } = useI18n();
  const [tab, setTab] = useState("summary");
  const TABS = useMemo(() => [["summary", tx("Özet"), BarChart3], ["flow", tx("Teklif → Fatura"), FileText], ["bank", tx("Banka"), Landmark],
    ["suppliers", tx("Tedarikçiler"), Truck], ["data", tx("İçe aktar"), Database]], [tx]);
  return (
    <div data-testid="panel-finance">
      <PanelHead title={tx("Finans")} sub={tx("Teklif, fatura, ödeme eşleme, raporlar ve teslimatlar · yönetim görünümü")} />
      <div className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-thin -mx-1 px-1" role="tablist" data-testid="finance-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`finance-tab-${id}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-sm transition-colors ${tab === id ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/10 text-white/60 hover:text-cyan-200 hover:border-cyan-400/30"}`}>
            <Icon size={13} />{label}
          </button>
        ))}
      </div>
      {tab === "summary" && <ReportView />}
      {tab === "flow" && <FlowView />}
      {tab === "bank" && <BankView />}
      {tab === "suppliers" && <SuppliersView />}
      {tab === "data" && <DataView />}
      <Advanced label={tx("Bu ekran ne yapar, ne yapmaz?")} testid="finance-limits">
        <div>{tx("Para transferi veya ödeme başlatma yoktur; banka verisi yalnızca okunur.")}</div>
        <div>{tx("E-postalar yalnızca inceleme + tek kullanımlık onayla gönderilir; hatırlatmalar otomatik gönderilmez.")}</div>
        <div>{tx("Raporlar yönetim amaçlıdır; yasal/vergi muhasebesi değildir.")}</div>
      </Advanced>
    </div>
  );
};

/* Billing profile — the ONLY source of legal/address/bank/VAT data on invoices. Nothing is pre-filled or guessed. */
export const BillingProfileCard = () => {
  const { tx } = useI18n();
  const [p, setP] = useState(null);
  const [miss, setMiss] = useState([]);
  useEffect(() => { api.get("/biz/billing-profile").then((r) => { setP(r.data.profile); setMiss(r.data.missing_for_invoice); }).catch(() => setP({})); }, []);
  const save = async () => {
    try { const r = await api.put("/biz/billing-profile", p); setP(r.data.profile); setMiss(r.data.missing_for_invoice); toast.success(tx("Kaydedildi")); }
    catch (e) { toast.error(errMsg(e, tx, "Kaydedilemedi")); }
  };
  if (!p) return <div className="text-xs text-white/40">{tx("Yükleniyor...")}</div>;
  const F = (k, label, props = {}) => <Field label={tx(label)} value={p[k] ?? ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} {...props} />;
  return (
    <div data-testid="billing-profile-card">
      {miss.length > 0 && <p className="text-[0.72rem] text-amber-200/90 mb-2">{tx("Fatura kesmek için eksik:")} {miss.map((m) => tx(m)).join(", ")}</p>}
      <div className="grid sm:grid-cols-2 gap-x-3">
        {F("legal_name", "Şirket yasal adı")}{F("trading_name", "Ticari ad")}
        {F("company_number", "Companies House no")}{F("contact_email", "Fatura e-postası")}
        {F("contact_phone", "Telefon")}{F("payment_terms_days", "Ödeme vadesi (gün)", { type: "number" })}
      </div>
      <label className="block mb-3"><span className="label-eyebrow block mb-1">{tx("Kayıtlı adres")}</span>
        <textarea value={p.registered_address || ""} onChange={(e) => setP({ ...p, registered_address: e.target.value })} className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white min-h-[60px]" /></label>
      <div className="grid sm:grid-cols-3 gap-x-3 items-end">
        <label className="block mb-3 text-xs text-white/60">{tx("KDV (VAT) durumu")}
          <select value={p.vat_status} onChange={(e) => setP({ ...p, vat_status: e.target.value })} className="w-full bg-black/40 border border-cyan-500/20 rounded px-2 py-2 text-white" data-testid="vat-status">
            <option value="unset">{tx("Ayarlanmadı")}</option><option value="registered">{tx("KDV kayıtlı")}</option><option value="not_registered">{tx("KDV kayıtlı değil")}</option></select></label>
        {p.vat_status === "registered" && F("vat_number", "VAT numarası")}
        {p.vat_status === "registered" && (
          <label className="flex items-center gap-2 text-xs text-white/70 mb-3"><input type="checkbox" checked={!!p.vat_verified_by_owner} onChange={(e) => setP({ ...p, vat_verified_by_owner: e.target.checked })} data-testid="vat-verified" />{tx("Bu VAT numarasını HMRC kaydımdan doğruladım")}</label>
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-x-3">
        {F("bank_account_name", "Banka hesap adı")}{F("bank_sort_code", "Sort code")}{F("bank_account_number", "Hesap numarası")}
        {F("bank_iban", "IBAN (opsiyonel)")}{F("bank_bic", "BIC (opsiyonel)")}{F("invoice_footer", "Fatura alt notu")}
      </div>
      <Btn onClick={save} data-testid="billing-save">{tx("Kaydet")}</Btn>
      <p className="text-[0.68rem] text-white/40 mt-2">{tx("Bu bilgiler yalnızca faturalarda kullanılır ve sizin girdiğiniz şekliyle basılır; JARVIS şirket, banka veya VAT bilgisi uydurmaz. Değişiklikler denetim kaydına yazılır (banka numaraları maskelenir).")}</p>
    </div>
  );
};

export default FinancePanel;
